import { toast } from "sonner";

/** The failed-request envelope `customFetch` attaches to a thrown `ApiCallError`. */
export type ApiErrorResponse = {
  data: unknown;
  /** Parsed response body, or `{ message }` when the body was not JSON. */
  error: unknown;
  status: number;
  args?: unknown;
};

export type ApiErrorDetail = {
  code?: string;
  message?: string | null;
};

/** Error body shape the API may return: an optional per-field `errors` map plus problem details. */
export type ApiProblemDetails = {
  errors?: Record<string | number, ApiErrorDetail[]>;
  type?: string | null;
  title?: string | null;
  status?: number | null;
  detail?: string | null;
  instance?: string | null;
};

export class ApiCallError extends Error {
  data: ApiErrorResponse;

  constructor(data: ApiErrorResponse) {
    super();
    this.data = data;
  }

  protected isApiException = true;

  static isApiException(obj: unknown): obj is ApiCallError {
    return !!obj && typeof obj === "object" && (obj as { isApiException?: unknown }).isApiException === true;
  }
}

export const getApiErrorPayload = (error: unknown): ApiProblemDetails | undefined => {
  if (!ApiCallError.isApiException(error)) return undefined;

  const payload = error.data?.error;
  if (!payload || typeof payload !== "object") return undefined;

  return payload as ApiProblemDetails;
};

const getApiErrorStatus = (error: unknown): number | undefined => {
  if (ApiCallError.isApiException(error)) return error.data?.status;

  if (!!error && typeof error === "object" && "status" in error && typeof error.status === "number") {
    return error.status;
  }

  return undefined;
};

const getErrorMessage = (error: unknown): string => {
  const payload = getApiErrorPayload(error);

  if (payload?.title) return payload.title;
  if (typeof payload?.detail === "string" && payload.detail.length > 0) return payload.detail;

  if (error instanceof ApiCallError) {
    const raw = error.data?.error;
    if (!!raw && typeof raw === "object" && "message" in raw && typeof raw.message === "string" && raw.message.length > 0) {
      return raw.message;
    }
  }

  if (error instanceof Error && error.message) return error.message;

  return "Při zpracování požadavku nastala chyba";
};

/** Surfaces server-side (5xx) failures as a toast. Client-side errors are handled at the call site. */
export const handleResponseError = (error: unknown) => {
  const status = getApiErrorStatus(error) ?? 500;

  if (status >= 500) {
    toast.error(getErrorMessage(error));
  }
};
